import { apiRequest } from "@/lib/api/http";
import type { InputListEntry } from "@/lib/telemetry/attrs";

/** What `/api/inputs` sends — the enrichment fields only, not the whole input row. */
interface MapInputResponse {
  device_type_id: string;
  input: string;
  description: string;
  multiplier: string;
  multiplier_custom?: string;
  unit: string;
}

/**
 * The `show_on_map` input definitions, keyed `${device_type_id}:${inputId}` — the same key
 * `fetchInputMap` builds server-side, because `processAttrs` looks inputs up by it.
 *
 * The response carries only the enrichment fields, so the flags are filled back in here:
 * `show_on_map` is `"1"` by construction (the endpoint sends nothing else), and `id` /
 * `alarm_type` are untouched by the enrichment path but required by the shared type.
 */
export async function listMapInputs(): Promise<Map<string, InputListEntry>> {
  const entries = await apiRequest<MapInputResponse[]>("/api/inputs");
  const map = new Map<string, InputListEntry>();
  if (!Array.isArray(entries)) return map;

  for (const entry of entries) {
    const deviceTypeId = entry?.device_type_id?.trim();
    const inputId = Number(entry?.input);
    if (!deviceTypeId || !Number.isFinite(inputId)) continue;

    map.set(`${deviceTypeId}:${inputId}`, {
      id: `${deviceTypeId}:${entry.input}`,
      input: entry.input,
      description: entry.description ?? "",
      multiplier: entry.multiplier ?? "",
      multiplier_custom: entry.multiplier_custom,
      unit: entry.unit ?? "",
      alarm_type: "",
      show_on_map: "1",
      device_type_id: deviceTypeId,
    });
  }

  return map;
}
